import { useEffect, useState } from "react"
import { motion } from "framer-motion"
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, Legend
} from "recharts"
import { TrendingUp, Activity, Dumbbell } from "lucide-react"
import { getClientProgress } from "../../api/progress"
import type { ClientProgress } from "../../types"

interface Props {
  clientId: number
  clientName: string
}

const EXERCISES_COLORS = [
  "#22d3ee", "#a78bfa", "#f472b6", "#34d399", "#fb923c", "#60a5fa"
]

export default function ProgressCharts({ clientId, clientName }: Props) {
  const [data, setData] = useState<ClientProgress | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<"body" | "frequency" | "strength">("body")

  useEffect(() => {
    getClientProgress(clientId)
      .then(setData)
      .finally(() => setLoading(false))
  }, [clientId])

  // Build strength data per exercise for multi-line chart
  const strengthByExercise = (() => {
    if (!data) return { dates: [], exercises: [], series: {} as Record<string, (number | null)[]> }
    const exercises = [...new Set(data.strength.map((s) => s.exercise))]
    const datesSet = [...new Set(data.strength.map((s) => s.date))].sort()
    const series: Record<string, (number | null)[]> = {}
    exercises.forEach((ex) => {
      series[ex] = datesSet.map((d) => {
        const entry = data.strength.find((s) => s.exercise === ex && s.date === d)
        return entry ? Number(entry.weight) : null
      })
    })
    return { dates: datesSet, exercises, series }
  })()

  const strengthChartData = strengthByExercise.dates.map((date, i) => {
    const row: Record<string, unknown> = { date }
    strengthByExercise.exercises.forEach((ex) => {
      row[ex] = strengthByExercise.series[ex][i]
    })
    return row
  })

  const tabs = [
    { key: "body" as const, label: "Cuerpo", icon: TrendingUp },
    { key: "frequency" as const, label: "Frecuencia", icon: Activity },
    { key: "strength" as const, label: "Fuerza", icon: Dumbbell },
  ]

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center text-muted text-sm">
        Cargando datos...
      </div>
    )
  }

  if (!data) return null

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white uppercase tracking-wide">
          Progreso — {clientName}
        </h3>
        <div className="flex gap-1 rounded-lg bg-surface-elevated p-1">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition ${
                activeTab === key ? "bg-primary text-black" : "text-muted hover:text-white"
              }`}
            >
              <Icon size={12} />
              {label}
            </button>
          ))}
        </div>
      </div>

      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="gradient-card rounded-xl border border-white/10 p-4"
      >
        {activeTab === "body" && (
          <>
            {data.body_metrics.length === 0 ? (
              <EmptyState message="Sin métricas corporales registradas" />
            ) : (
              <div className="space-y-6">
                <ChartSection title="Peso (kg)">
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={data.body_metrics}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 11 }} tickFormatter={fmtDate} />
                      <YAxis tick={{ fill: "#6b7280", fontSize: 11 }} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v: unknown) => [`${v} kg`, "Peso"]} />
                      <Line type="monotone" dataKey="weight" stroke="#22d3ee" strokeWidth={2} dot={{ r: 3, fill: "#22d3ee" }} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartSection>

                <ChartSection title="Composición corporal (%)">
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={data.body_metrics}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 11 }} tickFormatter={fmtDate} />
                      <YAxis tick={{ fill: "#6b7280", fontSize: 11 }} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v: unknown, name: unknown) => [`${v}%`, String(name)]} />
                      <Legend wrapperStyle={{ fontSize: 11, color: "#9ca3af" }} />
                      <Line type="monotone" dataKey="body_fat" name="Grasa %" stroke="#f472b6" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                      <Line type="monotone" dataKey="muscle_mass" name="Músculo %" stroke="#34d399" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartSection>
              </div>
            )}
          </>
        )}

        {activeTab === "frequency" && (
          <>
            {data.workout_frequency.length === 0 ? (
              <EmptyState message="Sin entrenamientos registrados en las últimas 12 semanas" />
            ) : (
              <ChartSection title="Entrenamientos por semana">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={data.workout_frequency}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="week" tick={{ fill: "#6b7280", fontSize: 11 }} tickFormatter={fmtDate} />
                    <YAxis tick={{ fill: "#6b7280", fontSize: 11 }} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v: unknown) => [String(v), "Entrenamientos"]} />
                    <Bar dataKey="count" fill="#22d3ee" radius={[4, 4, 0, 0]} opacity={0.85} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartSection>
            )}
          </>
        )}

        {activeTab === "strength" && (
          <>
            {strengthByExercise.exercises.length === 0 ? (
              <EmptyState message="Sin datos de fuerza registrados" />
            ) : (
              <ChartSection title="Progresión de peso por ejercicio (kg)">
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={strengthChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 11 }} tickFormatter={fmtDate} />
                    <YAxis tick={{ fill: "#6b7280", fontSize: 11 }} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v: unknown, name: unknown) => [`${v} kg`, String(name)]} />
                    <Legend wrapperStyle={{ fontSize: 10, color: "#9ca3af" }} />
                    {strengthByExercise.exercises.slice(0, 6).map((ex, i) => (
                      <Line
                        key={ex}
                        type="monotone"
                        dataKey={ex}
                        stroke={EXERCISES_COLORS[i % EXERCISES_COLORS.length]}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        connectNulls
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </ChartSection>
            )}
          </>
        )}
      </motion.div>
    </div>
  )
}

function ChartSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-3 text-xs text-muted uppercase tracking-wide">{title}</p>
      {children}
    </div>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex h-32 items-center justify-center text-muted text-sm">
      {message}
    </div>
  )
}

function fmtDate(value: string) {
  if (!value) return ""
  const d = new Date(value)
  return `${d.getDate()}/${d.getMonth() + 1}`
}

const tooltipStyle = {
  backgroundColor: "#1a1f2e",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: "8px",
  color: "#fff",
  fontSize: 12,
}
